import {ActivatedRoute, ParamMap, Router} from '@angular/router';
import {EntitySchemaService} from '@catcore/services/entity-schema.service';
import {GenericResourceInfo} from '@catcore/types/base';
import * as utils from '@catcore/utils';
import {ModelTypes} from '@catcore/utils/json-schema';
import {labels} from '@catcore/utils/json-schema/type-label-mapping';
import {GenericResourceService} from '@catfeatures/services/generic-resource.service';
import {JsonHalToExplorerTreeFactory} from '@catfeatures/shared/resource-navigation-factory';
import {
  buildQueryParamsForTargetNode,
  deserializeParamsForFeature,
  labelForResourceId,
  normalizeResourceToKey,
  serializeFeatureParams,
} from '@catfeatures/shared/utils';
import {BreadcrumbService} from '@catmodules/widgets/breadcrumb/services/breadcrumb.service';
import {Item} from '@catmodules/widgets/breadcrumb/types';
import {NodePathRef} from '@catmodules/widgets/explorer/types';
import {ExplorerItem, TreeNodeInfo} from '@catui/types';
import {BehaviorSubject} from 'rxjs/BehaviorSubject';
import {Observable} from 'rxjs/Observable';
import {isNullOrUndefined} from 'util';

const MAX_TREE_DEPTH = 3;
const DEFAULT_ROUTE_RESOURCE = 'resource';
const WIZARD_ROUTE_DISPATCH = 'resource-wizard';

const updateUrlQueryParams = (router: Router, nodeInfo: TreeNodeInfo) => {
  const urlTree = router.parseUrl(router.url);
  urlTree.queryParams = Object.assign(
    {},
    urlTree.queryParams,
    serializeFeatureParams(nodeInfo.params),
  );

  router.navigateByUrl(urlTree);
};

/**
 * Base class for currently every ExplorerTree with very specific customizations in it's behaviour and view elements.
 * This version is used in:
 * - the main Feature details section
 * - the Project / GUI Zone section
 */
export abstract class ExplorerCustomTree {
  explorerItems: ExplorerItem[] = [];
  doAllowCreate = false;

  treeNodeFactory: JsonHalToExplorerTreeFactory;
  defaultFactoryOptions = {routeTargets: {forRootNode: WIZARD_ROUTE_DISPATCH, forChildNode: DEFAULT_ROUTE_RESOURCE}};

  currentActiveItem: ExplorerItem;
  explorerItemsDirectForward: ExplorerItem[] = [];

  private currentTreeNodeChain: ExplorerItem[] = [];
  private queryParamsSubscription;
  private currentPathToRef: NodePathRef[];
  private resourceToExclude = new Map<string, string[]>();
  private maxTreeResolveDepth = MAX_TREE_DEPTH;

  constructor(protected route: ActivatedRoute,
              protected resourceService: GenericResourceService,
              protected schemaService: EntitySchemaService,
              protected breadcrumbService: BreadcrumbService,
              protected router: Router) {

    this.treeNodeFactory = new JsonHalToExplorerTreeFactory(
      this.schemaService,
    );
  }

  /**
   * Click handler for a custom defined TreeNode
   * Determines by itself if the child elements expand.
   * Update the url state with the position about the currently selected TreeNode.
   * Determines if a existing router forward should be executed or to be suppressed.
   *
   * @param {TreeNodeInfo} nodeInfo
   */
  processCustomTreeNode(nodeInfo: TreeNodeInfo, defaultRouteForward = false) {
    const maxDepthReached = this.isMaxDepthReached(nodeInfo.nodeLevel);
    let canBeExpanded = this.canBeExpanded(nodeInfo, defaultRouteForward);

    canBeExpanded = canBeExpanded && !maxDepthReached;
    canBeExpanded = canBeExpanded && nodeInfo.item.children && !nodeInfo.item.expanded;

    nodeInfo.item.expanded = canBeExpanded;
    updateUrlQueryParams(this.router, nodeInfo);
    let allowRouteForward = defaultRouteForward;

    if (maxDepthReached || this.explorerItemsDirectForward.includes(nodeInfo.item)) {
      allowRouteForward = true;
    }

    this.navigateToTargetRoute(nodeInfo, allowRouteForward);
  }

  protected canBeExpanded(nodeInfo: TreeNodeInfo, defaultRouteForward = false){
    return true;
  }

  /**
   * Default click handler for every not customized TreeNode
   * @param {TreeNodeInfo} nodeInfo
   */
  processDefaultTreeNode(nodeInfo: TreeNodeInfo) {
    this.navigateToTargetRoute(nodeInfo, true);
  }

  abstract getAllowCreateAction();

  abstract getAllowInfoAction();

  protected setTreeResolveDepth(value: number) {
    this.maxTreeResolveDepth = value;
  }

  protected excludeResource(parent: string, ref: string) {
    let refs = this.resourceToExclude.get(parent);
    if (!refs) {
      refs = [];
    }
    refs.push(ref);
    this.resourceToExclude.set(parent, refs);
  }

  protected isResourceExcluded(parent: string, ref: string) {
    const normalizedParentRef = normalizeResourceToKey(parent);
    const normalizedTargetRef = normalizeResourceToKey(ref);
    const refs = this.resourceToExclude.get(normalizedParentRef);
    return refs && refs.includes(normalizedTargetRef);
  }

  protected refreshNavigationalState(initNavigationalState = false) {
    if (isNullOrUndefined(this.queryParamsSubscription)) {
      this.initNavigationalState(initNavigationalState);
    } else {
      this.refreshNavigationState();
    }
  }

  /**
   * Initialize processing the navigational state.
   * In rare cases it is necessary to automaticly use the first item of the current ExplorerTree as the current active.
   * This cases can be:
   * - if the explorer opens with a default active item
   *
   * @param {boolean} useFirstAsDefault
   */
  protected initNavigationalState(useFirstAsDefault = false) {
    if (useFirstAsDefault) {
      this.currentActiveItem = this.explorerItems[0];
    }
    this.queryParamsSubscription = this.initiateQueryParamsListener();
  }

  private resolveNodeAndNavigateTo(nodeInfo: TreeNodeInfo, maxDepthReached: boolean, allowRouteNavigation: boolean): void {
    let routeCommand = nodeInfo.item.value.route.command;

    this
      .resolveTreeNodeChildrenByNode(nodeInfo, maxDepthReached)
      .subscribe((result) => {
        if (maxDepthReached && result.entityCouldHaveChildren) {
          routeCommand = 'resource-map';
        }

        let queryParams = buildQueryParamsForTargetNode(nodeInfo);

        if (!isNullOrUndefined(nodeInfo.params)) {
          queryParams = Object.assign({}, queryParams, serializeFeatureParams(nodeInfo.params));
        }

        allowRouteNavigation = allowRouteNavigation && !isNullOrUndefined(routeCommand);

        if (allowRouteNavigation) {
          this.router.navigate(
            routeCommand ? [routeCommand] : [],
            {relativeTo: this.route, queryParams},
          );
        }
      });
  }

  private navigateByRoute(nodeInfo: TreeNodeInfo, allowRouteNavigation: boolean): void {
    const routeCommand = nodeInfo.item.value.route.command;
    let queryParams = buildQueryParamsForTargetNode(nodeInfo);

    if (!isNullOrUndefined(nodeInfo.params)) {
      queryParams = Object.assign({}, queryParams, serializeFeatureParams(nodeInfo.params));
    }
    if (allowRouteNavigation) {
      this.router.navigate(
        routeCommand ? [routeCommand] : [],
        {relativeTo: this.route, queryParams},
      );
    }
  }

  /**
   * Pointing the router to a new route, based on the provided node information and if it is allowed to change the routing.
   *
   * @param {TreeNodeInfo} nodeInfo
   * @param {boolean} allowRouteForward
   */
  protected navigateToTargetRoute(nodeInfo: TreeNodeInfo, allowRouteForward = true) {

    const maxDepthReached = !(nodeInfo.nodeLevel < this.maxTreeResolveDepth);
    const routeCommand = nodeInfo.item.value.route.command;
    const allowRouteNavigation = allowRouteForward;

    if (maxDepthReached && allowRouteNavigation) {
      this.navigateByRoute(nodeInfo, allowRouteNavigation);
    } else {
      this.resolveNodeAndNavigateTo(nodeInfo, maxDepthReached, allowRouteNavigation);
    }

  }

  /**
   * If the component has a valid navigation reference path (currentPathRef) and items for the navigation (explorerItems),
   * the function try to recreate the state of each tree node to represent the navigation reference path in the displayed navigation.
   */
  private refreshNavigationState() {
    if (!isNullOrUndefined(this.currentPathToRef) && !isNullOrUndefined(this.explorerItems)) {
      this.resetCurrentTreeNodeChain();
      const pathToRef = [...this.currentPathToRef];
      this.lookupCurrentActiveItem(pathToRef, this.explorerItems);
    }
  }

  /**
   * Initiates a listener to the common URL Query Params
   * If the query params which conrols the navigation flow are changed, it should resolve in rebuilding the navigation tree, and selecting
   * the currently active element, for the current selected feature.
   *
   * @returns {Subscription}
   */
  protected initiateQueryParamsListener() {
    return this.route.queryParamMap.subscribe((params: ParamMap) => {
      const nodeRefParams = deserializeParamsForFeature(params);
      const hasRefParams = !isNullOrUndefined(nodeRefParams) && !isNullOrUndefined(nodeRefParams.pathToRef);
      if (hasRefParams) {
        this.currentPathToRef = [...nodeRefParams.pathToRef];
        this.refreshNavigationState();
      }
    });
  }

  private resetCurrentTreeNodeChain() {
    this.currentTreeNodeChain = [];
  }

  /**
   * Based on the provided indexPathList, the function goe through the list try to match every single entry with an element from
   * the the current navigation tree.
   *
   * @param {NodePathRef[]} indexPathList
   * @param {ExplorerItem[]} items
   */
  private lookupCurrentActiveItem(indexPathList: NodePathRef[], items: ExplorerItem[], initialPathLength = null) {
    console.log('lookupCurrentActiveItem: ', indexPathList.map((i) => i.id).join(', '));
    const totalPathLength = !isNullOrUndefined(initialPathLength) ? initialPathLength : indexPathList.length;
    const ref = indexPathList.shift();
    const isValidByIndex = !isNullOrUndefined(ref) && !isNullOrUndefined(items[ref.index]);
    const canBeExpanded = !(this.isMaxDepthReached(totalPathLength) && indexPathList.length === 0);

    let currentActiveItem = null;

    if (isValidByIndex) {
      currentActiveItem = items[ref.index];
    }

    if (!isNullOrUndefined(currentActiveItem)) {
      this.currentActiveItem = currentActiveItem;
      this.currentActiveItem.expanded = canBeExpanded;
      this.currentTreeNodeChain.push(this.currentActiveItem);
    }

    const hasChildren = !isNullOrUndefined(this.currentActiveItem.children) && this.currentActiveItem.children.length > 0;

    if (indexPathList.length > 0) {
      this.resolveTreeNodeChildren(this.currentActiveItem, false)
        .subscribe((resource) => {
          if (isNullOrUndefined(resource)) {
            console.warn('resolved resource is null', resource);
          } else {
            this.lookupCurrentActiveItem(indexPathList, this.currentActiveItem.children, totalPathLength);
          }
        });
    } else {
      this.updateBreadcrumbsByChain(this.currentTreeNodeChain);
    }
  }

  private updateBreadcrumbsByChain(treeNodeChain: ExplorerItem[]) {
    const breadcrumbs: Item[] = [];
    treeNodeChain.forEach((item: ExplorerItem) => breadcrumbs.push({label: item.label, value: item}));
    this.breadcrumbService.update(breadcrumbs);
  }

  private resolveTreeNodeChildrenByNode(nodeInfo: TreeNodeInfo, maxDepthReached: boolean): Observable<any> {
    const item = nodeInfo.item;
    return this.resolveTreeNodeChildren(item, maxDepthReached, nodeInfo.nodeLevel);
  }

  private resolveTreeNodeChildren(item: ExplorerItem, maxDepthReached: boolean, nodeLevel = 0): Observable<any> {
    const resourceId = item.value.route.resourceId;
    let isEntityList = false;
    let entityCouldHaveChildren = false;
    let allowLazyLoadChildren = !(maxDepthReached);
    allowLazyLoadChildren = allowLazyLoadChildren && (!item.children || !item.children.length);
    allowLazyLoadChildren = allowLazyLoadChildren && item.value.route.resourceId;

    let defaultResult = new BehaviorSubject({
      item,
      childrenLoaded: false,
      entityCouldHaveChildren,
    }).asObservable();

    if (!isNullOrUndefined(resourceId)) {
      entityCouldHaveChildren = utils.resourceCouldHaveChildren(
        resourceId,
        this.schemaService,
      );
      isEntityList = utils.isEntityList(resourceId);
    }

    if (!isEntityList && allowLazyLoadChildren) {
      const service = this.resourceService.findServiceForResource(item.value.route.resourceId);
      defaultResult = service
        .getCurrent()
        .map((resource: GenericResourceInfo) => {
          if (!isNullOrUndefined(resource)) {
            const itemLabel = labelForResourceId(item.value.route.resourceId);
            const beforeChildren = isNullOrUndefined(item.children) ? [] : [...item.children];
            const maxDepthInReach = this.isMaxDepthReached(nodeLevel + 1);
            const afterChildren = this.treeNodeFactory
              .getForRoot(
                resource,
                {label: () => itemLabel, ...this.defaultFactoryOptions},
                item.filter,
              )
              .filter((explorerItem: ExplorerItem) => {
                const parentRef = resource._links.self.href;
                const targetRef = explorerItem.value.route.resourceId;
                return !this.isResourceExcluded(parentRef, targetRef);
              });

            item.children = normalizeItemStates(beforeChildren, afterChildren);
            // .map((explorerItem: ExplorerItem) => {
            //   explorerItem.children = maxDepthInReach ? [] : explorerItem.children;
            //   return explorerItem;
            // });
            item.expanded = true;

            this.annotateWithActions(item.children);

            return {item, childrenLoaded: true, entityCouldHaveChildren};
          }
        });
      service.load();
    }

    return defaultResult;
  }

  private isMaxDepthReached(current: number) {
    return current >= this.maxTreeResolveDepth;
  }

  private annotateWithActions(items: ExplorerItem[]) {
    items.forEach((item: ExplorerItem) => {
      const hasTargetDef = !isNullOrUndefined(item.value.route);
      let doAllowCreate = hasTargetDef && item.value.route.command === WIZARD_ROUTE_DISPATCH;
      doAllowCreate = doAllowCreate && canBeCreatedList.includes(item.value.navItem.ref);
      const doAllowInfo = hasTargetDef && !doAllowCreate && item.value.route.command === WIZARD_ROUTE_DISPATCH;

      if (doAllowCreate) {
        item.allowedActions.push(this.getAllowCreateAction());
      }
      if (doAllowInfo) {
        item.allowedActions.push(this.getAllowInfoAction());
      }

    });
  }
}

const canBeCreatedList = ((): string[] => {
  let allowedByKeys: string[] = [];
  const listOfModels = [
    ModelTypes.FeatureEvidTimer,
    ModelTypes.FeatureEvidVid,
  ];

  listOfModels.forEach((typeName: string) => {
    const labelDefinition = labels.get(typeName);

    if (!isNullOrUndefined(labelDefinition)) {
      allowedByKeys = [...allowedByKeys, ...labelDefinition.matchingPropertyNames];
    }
  });

  return allowedByKeys;
})();

const normalizeItemStates = (beforeChildren: ExplorerItem[], afterChildren: ExplorerItem[]): ExplorerItem[] => {
  beforeChildren.forEach((beforeItem: ExplorerItem) => {
    afterChildren.forEach((afterItem: ExplorerItem) => {
      if (!isNullOrUndefined(beforeItem.equals) && beforeItem.equals(afterItem)) {
        afterItem.expanded = beforeItem.expanded;
      }
    });
  });

  return afterChildren;
};



// WEBPACK FOOTER //
// /agent/workspace/CUS-LHT_NICE_nto_nto_CAT_release@2/app/Resources/Public/src/app/shared/explorer-custom-tree.ts